<script setup lang="ts">
import { nextTick, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref } from 'vue';
import ActionButton from '@/components/ui/ActionButton.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import type { IconName } from '@/libs/icons';
import { useAppStore } from '@/stores/app';

const emit = defineEmits<{
  /** Fired once the exit animation is over: the parent can drop the component. */
  close: [];
}>();

const ARGUMENTS: ReadonlyArray<{ icon: IconName; key: string }> = [
  { icon: 'bookmark', key: 'favoritesOnboarding.argument1' },
  { icon: 'clock', key: 'favoritesOnboarding.argument2' },
];

const appStore = useAppStore();

const sheetEl = ref<HTMLElement>();
const bodyEl = ref<HTMLElement>();
const open = ref<boolean>(true);

// Android back button closes the sheet instead of leaving the Schedules tab.
// Schedules is kept alive: the handler must follow activation, or it would
// swallow the first back press of the next page.
let unregisterBackHandler: (() => void) | undefined;

function registerBackHandler() {
  if (!open.value) return;
  unregisterBackHandler ??= appStore.registerBackHandler(() => {
    dismiss();
    return true;
  });
}

function unregisterBackHandlerIfAny() {
  unregisterBackHandler?.();
  unregisterBackHandler = undefined;
}

function dismiss() {
  open.value = false;
  unregisterBackHandlerIfAny();
}

// The sheet opens on its own: move the focus in so assistive tech announces
// it, and hand it back on close so the user keeps their place in the page.
let previousFocus: HTMLElement | null = null;

onMounted(() => {
  registerBackHandler();
  previousFocus = document.activeElement as HTMLElement | null;
  nextTick(() => sheetEl.value?.focus({ preventScroll: true }));
});

function onAfterLeave() {
  previousFocus?.focus({ preventScroll: true });
  emit('close');
}
onActivated(registerBackHandler);
onDeactivated(unregisterBackHandlerIfAny);

onBeforeUnmount(() => {
  unregisterBackHandlerIfAny();
  releasePointerPull?.();
});

// #region Pull-down gesture
// A flick, in px/ms, or a pull past this distance, dismisses the sheet.
const FLICK_VELOCITY = 0.15;
const DISMISS_DISTANCE = 80;
// The first touchmove is often a jitter of a pixel or two: wait for a direction.
const DIRECTION_DEAD_ZONE = 4;

const pullOffset = ref<number>(0);
const isPulling = ref<boolean>(false);

let startY = 0;
let lastY = 0;
let lastTime = 0;
let velocity = 0;
// Whether the touch sequence pulls the sheet (vs scrolls the body); undecided until a direction is known.
let touchCaptured: boolean | undefined;
// Tears down the window listeners of a mouse pull, from wherever it ends.
let releasePointerPull: (() => void) | undefined;

function startPull(y: number) {
  startY = y;
  lastY = y;
  lastTime = performance.now();
  velocity = 0;
}

function movePull(y: number) {
  const now = performance.now();
  if (now > lastTime) velocity = (y - lastY) / (now - lastTime);
  lastY = y;
  lastTime = now;
  pullOffset.value = Math.max(y - startY, 0);
}

function endPull() {
  isPulling.value = false;
  if (velocity > FLICK_VELOCITY || pullOffset.value > DISMISS_DISTANCE) dismiss();
  else pullOffset.value = 0;
}

function onTouchStart(event: TouchEvent) {
  touchCaptured = undefined;
  startPull(event.touches[0].clientY);
}

function onTouchMove(event: TouchEvent) {
  const y = event.touches[0].clientY;
  if (touchCaptured === undefined) {
    const delta = y - startY;
    if (Math.abs(delta) < DIRECTION_DEAD_ZONE) return;
    // A scrolled body scrolls back up first; the sheet only follows a finger
    // pulling down from the top.
    const inBody = bodyEl.value?.contains(event.target as Node) ?? false;
    touchCaptured = delta > 0 && !(inBody && (bodyEl.value?.scrollTop ?? 0) > 0);
    if (!touchCaptured) return;
    isPulling.value = true;
    startY = y;
  }
  if (!touchCaptured) return;
  if (event.cancelable) event.preventDefault();
  movePull(y);
}

function onTouchEnd() {
  if (touchCaptured) endPull();
  touchCaptured = undefined;
}

function onGrabberPointerDown(event: PointerEvent) {
  // Touch is handled above (it needs preventDefault on touchmove); pointer events cover the mouse only.
  if (event.pointerType !== 'mouse') return;
  isPulling.value = true;
  startPull(event.clientY);
  const onMove = (e: PointerEvent) => movePull(e.clientY);
  const onUp = () => {
    endPull();
    releasePointerPull?.();
  };
  // `pointercancel` too: the browser taking the pointer back would otherwise
  // leave the listeners on `window` and the sheet stuck mid-pull.
  releasePointerPull = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    releasePointerPull = undefined;
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
}
// #endregion
</script>

<template>
  <Teleport to="body">
    <!-- The veil and the sheet animate, not the root: Vue cannot read the duration off it -->
    <Transition
      name="favorites-onboarding"
      appear
      :duration="{ enter: 300, leave: 200 }"
      @afterLeave="onAfterLeave"
    >
      <div v-if="open" class="favorites-onboarding">
        <div class="favorites-onboarding__veil" @click="dismiss" />

        <div
          ref="sheetEl"
          class="favorites-onboarding__sheet"
          :class="{ 'favorites-onboarding__sheet--pulling': isPulling }"
          :style="{ translate: `0 ${pullOffset}px` }"
          role="dialog"
          aria-modal="true"
          aria-labelledby="favorites-onboarding-title"
          tabindex="-1"
          @keydown.esc="dismiss"
          @touchstart.passive="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
          @touchcancel="onTouchEnd"
        >
          <div class="favorites-onboarding__grabber-zone" @pointerdown="onGrabberPointerDown">
            <div class="favorites-onboarding__grabber" />
          </div>

          <div ref="bodyEl" class="favorites-onboarding__body">
            <div class="favorites-onboarding__illustration" aria-hidden="true">
              <div
                v-for="card in ['back', 'front']"
                :key="card"
                class="favorites-onboarding__card"
                :class="`favorites-onboarding__card--${card}`"
              >
                <div class="favorites-onboarding__card-row">
                  <div class="favorites-onboarding__card-details">
                    <div class="favorites-onboarding__block favorites-onboarding__block--badge" />
                    <div class="favorites-onboarding__card-title">
                      <div class="favorites-onboarding__block favorites-onboarding__block--icon" />
                      <div class="favorites-onboarding__block favorites-onboarding__block--title" />
                    </div>
                  </div>
                  <div class="favorites-onboarding__card-tags">
                    <div class="favorites-onboarding__block favorites-onboarding__block--icon" />
                    <div class="favorites-onboarding__block favorites-onboarding__block--tag" />
                  </div>
                </div>
              </div>
              <BaseIcon
                name="bookmark"
                :size="47"
                class="favorites-onboarding__bookmark favorites-onboarding__bookmark--large"
              />
              <BaseIcon
                name="bookmark"
                :size="27"
                class="favorites-onboarding__bookmark favorites-onboarding__bookmark--small"
              />
            </div>

            <div class="favorites-onboarding__text">
              <h2 id="favorites-onboarding-title" class="favorites-onboarding__title">
                {{ $t('home.favorites.empty.title') }}
              </h2>
              <p class="favorites-onboarding__description">{{ $t('home.favorites.empty.subtitle') }}</p>
            </div>

            <div class="favorites-onboarding__arguments">
              <div v-for="argument in ARGUMENTS" :key="argument.key" class="favorites-onboarding__argument">
                <RoundedButton
                  :icon="argument.icon"
                  type="ghost"
                  class="favorites-onboarding__argument-icon"
                  tabindex="-1"
                  aria-hidden="true"
                />
                <span class="favorites-onboarding__argument-text">{{ $t(argument.key) }}</span>
              </div>
            </div>
          </div>

          <div class="favorites-onboarding__footer">
            <ActionButton
              type="brand"
              size="large"
              class="favorites-onboarding__cta"
              :label="$t('favoritesOnboarding.continue')"
              @click="dismiss"
            />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss">
.favorites-onboarding {
  display: flex;
  flex-direction: column;
  inset: 0;
  justify-content: flex-end;
  position: fixed;
  // Over the tab bar (2) and the pages, under the toast (10) and the popup (20).
  z-index: 5;

  &__veil {
    background-color: rgba(0, 0, 0, 0.4);
    inset: 0;
    position: absolute;
  }

  &__sheet {
    background-color: $color-white;
    border-radius: 38px 38px 0 0;
    box-shadow: 0 15px 75px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    // Leaves the status bar and a strip of the page visible on a short viewport.
    max-height: calc(100% - var(--status-bar-band, 0px) - #{$spacing-6});
    outline: none;
    position: relative;
    transition: translate 0.3s ease;

    &--pulling {
      transition: none;
    }
  }

  &__grabber-zone {
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    padding: 6px 0 2px;
    touch-action: none;
  }

  &__grabber {
    background-color: $color-neutral-medium;
    border-radius: $radius-pill;
    height: 5px;
    width: 36px;
  }

  &__body {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 40px;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 40px $spacing-4 0;
  }

  &__footer {
    flex-shrink: 0;
    // The mockup's 52px already hosts the iOS home indicator: grow only when the inset is taller.
    padding: 40px $spacing-4 calc(#{$spacing-5} + max(#{$spacing-6}, env(safe-area-inset-bottom, 0px)));
  }

  &__cta {
    width: 100%;
  }

  // #region Illustration
  &__illustration {
    flex-shrink: 0;
    height: 123px;
    max-width: 332px;
    position: relative;
    width: 100%;
  }

  &__card {
    background-color: $color-white;
    border: 1px solid $color-neutral-low;
    border-radius: $radius-3xl;
    overflow: hidden;
    padding: $spacing-1 0;
    position: absolute;

    &--back {
      left: $spacing-2;
      right: $spacing-2;
      top: 20px;
    }

    &--front {
      box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.04);
      left: 0;
      right: 0;
      top: 43px;
    }
  }

  &__card-row {
    align-items: flex-start;
    display: flex;
    gap: $spacing-3;
    padding: $spacing-3 $spacing-4;
  }

  &__card-details {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    gap: $spacing-1;
    min-width: 0;
  }

  &__card-title,
  &__card-tags {
    align-items: center;
    display: flex;
    gap: $spacing-1;
  }

  &__block {
    background-color: #e6e9ef;
    border-radius: $radius-tag;
    flex-shrink: 0;

    &--badge {
      height: 20px;
      width: 39px;
    }

    &--icon {
      height: 24px;
      width: 24px;
    }

    &--title {
      flex: 1 1 0;
      height: 24px;
    }

    &--tag {
      height: 24px;
      width: 74px;
    }
  }

  &__bookmark {
    color: $color-primary-darkest;
    fill: currentColor;
    filter: drop-shadow(0 -2px 3px rgba(0, 0, 0, 0.04));
    position: absolute;

    &--large {
      right: -10px;
      top: 17px;
      transform: rotate(15deg);
    }

    &--small {
      left: -18px;
      top: 39px;
      transform: rotate(-15deg);
    }
  }
  // #endregion

  &__text {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    text-align: center;
    width: 100%;
  }

  &__title {
    color: $color-neutral-highest;
    font: $heading-h1;
    letter-spacing: $heading-h1-spacing;
    margin: 0;
  }

  &__description,
  &__argument-text {
    color: #6b7280;
    font: $text-medium;
    letter-spacing: $text-medium-spacing;
    margin: 0;
  }

  &__arguments {
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
  }

  &__argument {
    align-items: flex-start;
    display: flex;
    gap: $spacing-3;
  }

  &__argument-icon {
    pointer-events: none;

    .rounded-button__icon {
      font-size: 16px;
    }
  }

  &__argument-text {
    flex: 1 1 0;
    // Aligned on the first line of a 40px icon box
    padding-top: $spacing-2;
  }

  &-enter-active {
    .favorites-onboarding__veil {
      transition: opacity $duration-drawer-open $easing-drawer;
    }

    .favorites-onboarding__sheet {
      transition: transform $duration-drawer-open $easing-drawer;
    }
  }

  &-leave-active {
    .favorites-onboarding__veil {
      transition: opacity $duration-drawer-close $easing-drawer;
    }

    .favorites-onboarding__sheet {
      transition: transform $duration-drawer-close $easing-drawer;
    }
  }

  &-enter-from,
  &-leave-to {
    .favorites-onboarding__veil {
      opacity: 0;
    }

    .favorites-onboarding__sheet {
      transform: translateY(100%);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .favorites-onboarding-enter-active,
  .favorites-onboarding-leave-active {
    .favorites-onboarding__veil,
    .favorites-onboarding__sheet {
      transition: none;
    }
  }
}
</style>
